import { loadableReady } from '@loadable/component';
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { applyMiddleware, compose, createStore } from 'redux';
import clientFrontend from '../../../../../shared/frontend/bundles/clientFrontend';
import {
  clearScreens,
  initializeAppData,
  showErrorScreen
} from '../../../../../shared/frontend/libs/appInitialization';
import commonFrontend from '../../../../../shared/frontend/libs/commonFrontend';
import { enableSSRDebugging } from '../../../../../shared/frontend/libs/enableSSRDebugging';
import historySystem from '../../../../../shared/frontend/libs/history';
import initPasteListener from '../../../../../shared/frontend/libs/pasteListener';
import { createRouterMiddleware } from '../../../../../shared/frontend/libs/router/redux-store';
import {
  localizeRoutes,
  mapRoutesToReactRouterRoutes,
  RoutesProvider
} from '../../../../../shared/frontend/libs/router/router-helpers';
import sagaMiddleware from '../../../../../shared/frontend/libs/sagaMiddleware';
import '../../../../../shared/frontend/libs/shims';
import '../../../../../shared/frontend/libs/windowHooks';
import '../../../../../shared/frontend/scripts/adblockDetector'; // Multi-layer adblock detection
import { I18nProvider } from '../../../../../shared/frontend/providers/I18nProvider/I18nProvider';
import ClientNetworkProvider from '../../../../../shared/frontend/scripts/services/clientNetworkProvider';
import { networkService } from '../../../../../shared/frontend/scripts/services/networkService';
import createRootReducer from '../reducers/apiReducer';
import rootSaga from '../sagas';
import { getRoutes } from './getRoutes';

networkService.init(new ClientNetworkProvider());

const middleware: any[] = [sagaMiddleware];

let composeEnhancers = compose;

if (IS_DEVELOPMENT) {
  const { createLogger } = require('redux-logger');
  middleware.push(createLogger({ collapsed: true }));
  if ((window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) {
    composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;
  }
}

let initialData = JSON.parse((window as any).__RIO_INITIAL_DATA);

(async function initializeApp() {
  try {
    await initializeAppData({ initialData });

    const routes = getRoutes(initialData.siteConfig);
    const localizedRoutes = localizeRoutes(routes);
    const reactRouterRoutes = mapRoutesToReactRouterRoutes(localizedRoutes);
    const router = createBrowserRouter(reactRouterRoutes);

    // Add router middleware to sync history changes with Redux
    middleware.push(createRouterMiddleware(router));

    // TODO: separate store configuration based on environment as shown here:
    // https://github.com/gaearon/redux-devtools/blob/master/docs/Walkthrough.md#exclude-devtools-from-production-builds
    //
    const store = createStore<any, any, any, any>(
      createRootReducer(router) as any,
      initialData,
      composeEnhancers(applyMiddleware(...middleware))
    );

    // grab some info we use for reamaze tickets
    (window as any).__RIO_CONFIG = {
      auth: initialData.auth?.authenticated ? `${initialData.auth.name} (${initialData.auth.id})` : 'Not Logged In',
      displayAdsProvider: initialData.siteConfig.displayAdsProvider || 'N/A',
      videoAdsProvider: initialData.siteConfig.videoAdsProvider || 'N/A'
    };

    (window as any).RIO_SITE = initialData.siteMeta.site;
    (window as any).RIO_ALL_SITES = initialData.siteMeta.allSites;

    // console.debug(`Raider.IO Site Configuration`, {
    //   RIO_SITE,
    //   RIO_ALL_SITES
    // });

    commonFrontend.init(store);
    clientFrontend.init(store, router);
    sagaMiddleware.run(rootSaga);
    initPasteListener(router);
    historySystem.init(router);

    function WrappedApp() {
      const app = (
        <HelmetProvider>
          <Provider store={store}>
            <I18nProvider>
              <RoutesProvider.Provider value={{ routes: reactRouterRoutes }}>
                <RouterProvider router={router} />
              </RoutesProvider.Provider>
            </I18nProvider>
          </Provider>
        </HelmetProvider>
      );

      return IS_DEVELOPMENT ? <StrictMode>{app}</StrictMode> : app;
    }

    loadableReady(() => {
      if (ENABLE_SSR_DEBUGGING) {
        enableSSRDebugging();
      }

      // Clear any loading/error screens before rendering main app
      clearScreens();

      const root = document.getElementById('application');
      if (!root) {
        throw new Error('FATAL: application root element not found');
      }

      const shouldHydrate = root.hasChildNodes();

      if (shouldHydrate) {
        hydrateRoot(root, <WrappedApp />);
      } else {
        createRoot(root).render(<WrappedApp />);
      }
    });

    // free up resources
    initialData = null;
    delete (window as any).__RIO_INITIAL_DATA;
  } catch (err) {
    console.error('Fatal error initializing application:', err);

    const errorMessage = err instanceof Error ? err.message : 'An error occurred while initializing the application.';
    showErrorScreen('Failed to initialize application', errorMessage);

    // error already logged & shown - so don't bubble it up to the user any further
    return;
  }
})();
